import React, { FC, PropsWithChildren } from 'react'
import { ComboboxItem } from '@ariakit/react'
import { breakpoint, css, styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { OnSubmit, SelectableRow, SubmitItemType } from '../types'

const BasicListItemStyles = css`
  cursor: pointer;
  ${s({ p: 3, m: 3, color: 'colorTextLabel', borderRadius: 2 })}

  &[data-active-item="true"],
  &:hover,
  &:focus {
    ${s({ bgcolor: 'colorBackground02' })}
  }

  &:active {
    ${s({ bgcolor: 'colorBackgroundSelectedState' })}
  }
`

const StyledListItem = styled(ComboboxItem)`
  ${BasicListItemStyles}
`

const StyledInlineListItem = styled(ComboboxItem)`
  cursor: pointer;
  ${s({
    display: 'inline-block',
    bgcolor: 'colorBackground01',
    border: '1px solid',
    borderColor: 'colorBorder03',
    borderRadius: 2,
    color: 'colorTextLabel',
    py: 4,
    pl: 4,
    pr: 6,
    mr: 4,
    mb: 4,
  })}

  &[data-active-item="true"],
  &:hover,
  &:focus {
    ${s({
      bgcolor: 'colorBackground02',
    })}
  }

  &[data-active-item='true'],
  &:focus,
  &:focus-visible {
    ${s({
      borderColor: 'colorInteractionFocusIndicator',
    })}
  }

  &:active {
    ${s({
      bgcolor: 'colorBackgroundSelectedState',
      borderColor: 'colorBackgroundSelectedState',
    })}
  }
`

const StyledListItemWithRemoveButton = styled(ComboboxItem)`
  ${BasicListItemStyles}

  ${breakpoint('md')} {
    ${s({ p: 3 })}

    button {
      display: none;
    }

    &[data-active-item='true'] button,
    &:hover button,
    &:focus-within button {
      display: flex;
    }
  }
`

type Props = PropsWithChildren<{
  row: SelectableRow
  firstListItemlRef?: React.RefObject<HTMLDivElement | null>
  onSubmit: OnSubmit
}>

export const ListItem: FC<Props> = ({
  row,
  firstListItemlRef,
  onSubmit,
  children,
}) => {
  let Component = null

  if (row.kind === 'inspiration') {
    Component = StyledInlineListItem
  } else if (row.kind === 'recent') {
    Component = StyledListItemWithRemoveButton
  } else {
    Component = StyledListItem
  }

  return (
    <Component
      value={row.item.value as string}
      id={row.id}
      ref={firstListItemlRef as React.RefObject<HTMLDivElement>}
      onClick={() => onSubmit(row.item as SubmitItemType)}
      aria-keyshortcuts={row.kind === 'recent' ? 'Delete Backspace' : undefined}
    >
      {children}
    </Component>
  )
}
